import { useEffect } from "react";
import { lockDocumentScroll } from "../services/utility/DocumentScrollLock";

/**
 * A hook that robustly prevents background scrolling when a modal or overlay is open.
 * It uses the position: fixed technique to freeze the entire page (handling nested scroll containers),
 * and blocks touchmove events on the overlay to prevent iOS Safari bounce-through.
 *
 * @param isLocked Whether the background scroll should be locked.
 * @param overlayRef Optional ref to the overlay element to block touchmove events.
 * @param scrollableClass Optional CSS selector for the inner container that IS allowed to scroll.
 */
const useScrollLock = (
  isLocked: boolean,
  overlayRef?: React.RefObject<HTMLElement>,
  scrollableClass: string = ".modal-scrollable"
) => {
  // 1. Lock ALL background scrolling
  useEffect(() => {
    if (!isLocked) return;

    return lockDocumentScroll();
  }, [isLocked]);

  // 2. Block touch-scroll on overlay (prevents iOS bounce-through)
  useEffect(() => {
    if (!isLocked || !overlayRef || !overlayRef.current) return;
    const overlay = overlayRef.current;

    const blockScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow scrolling inside the modal content itself
      if (target.closest(scrollableClass)) return;
      e.preventDefault();
    };

    overlay.addEventListener("touchmove", blockScroll, { passive: false });
    return () => overlay.removeEventListener("touchmove", blockScroll);
  }, [isLocked, overlayRef, scrollableClass]);
};

export default useScrollLock;
